import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  CreateAgentExecutionResponse,
  GetAgentExecutionByIdResponse,
  GetAgentExecutionsQuery,
  GetAgentExecutionsResponse,
  UpdateAgentExecutionResponse,
} from '@mai/platform-api/src/routes/agent-executions.schema';
import type {
  AgentExecution,
  CreateAgentExecution as CreateAgentExecutionType,
  UpdateAgentExecution as UpdateAgentExecutionType,
} from '@mai/types';

import { type QueryOptions, apiClient } from './axios';

// Type definitions from platform API schemas
export type GetAgentExecutionsRequestType = GetAgentExecutionsQuery;
export type CreateAgentExecutionRequest = CreateAgentExecutionType;
export type UpdateAgentExecutionRequest = UpdateAgentExecutionType;
export type {
  AgentExecution,
  CreateAgentExecutionResponse,
  UpdateAgentExecutionResponse,
};

// API functions
export const fetchAgentExecutions = async (
  params?: GetAgentExecutionsRequestType,
): Promise<GetAgentExecutionsResponse> => {
  const response = await apiClient.get('/agent-executions', { params });
  return response.data;
};

export const fetchAgentExecutionsByAgentId = async (
  agentId: string,
  params?: Omit<GetAgentExecutionsRequestType, 'agentIds'>,
): Promise<GetAgentExecutionsResponse> => {
  return fetchAgentExecutions({
    ...params,
    agentIds: [agentId],
    page: 1,
    pageSize: 100,
  });
};

export const fetchAgentExecutionsByTeamId = async (
  teamId: string,
  params?: Omit<GetAgentExecutionsRequestType, 'teamIds'>,
): Promise<GetAgentExecutionsResponse> => {
  return fetchAgentExecutions({
    ...params,
    teamIds: [teamId],
    page: 1,
    pageSize: 100,
  });
};

export const fetchAgentExecutionById = async (
  executionId: string,
): Promise<GetAgentExecutionByIdResponse> => {
  const response = await apiClient.get(`/agent-executions/${executionId}`);
  return response.data;
};

export const createAgentExecution = async (
  data: CreateAgentExecutionRequest,
): Promise<CreateAgentExecutionResponse> => {
  const response = await apiClient.post('/agent-executions', data);
  return response.data;
};

export const updateAgentExecution = async (
  executionId: string,
  data: UpdateAgentExecutionRequest,
): Promise<UpdateAgentExecutionResponse> => {
  const response = await apiClient.patch(
    `/agent-executions/${executionId}`,
    data,
  );
  return response.data;
};

// React Query hooks
export const useAgentExecutions = (
  params?: GetAgentExecutionsRequestType,
  options?: QueryOptions,
) => {
  return useQuery({
    queryKey: ['agent-executions', params],
    queryFn: () => fetchAgentExecutions(params),
    enabled: options?.enabled,
    retry: options?.retry,
  });
};

export const useAgentExecutionsByAgentId = (
  agentId: string | null,
  params?: Omit<GetAgentExecutionsRequestType, 'agentIds'>,
  options?: QueryOptions,
) => {
  return useQuery({
    queryKey: ['agent-executions', 'by-agent', agentId, params],
    queryFn: () => fetchAgentExecutionsByAgentId(agentId!, params),
    enabled: !!agentId && options?.enabled !== false,
    retry: options?.retry,
  });
};

export const useAgentExecutionsByTeamId = (
  teamId: string | null,
  params?: Omit<GetAgentExecutionsRequestType, 'teamIds'>,
  options?: QueryOptions,
) => {
  return useQuery({
    queryKey: ['agent-executions', 'by-team', teamId, params],
    queryFn: () => fetchAgentExecutionsByTeamId(teamId!, params),
    enabled: !!teamId && options?.enabled !== false,
    retry: options?.retry,
  });
};

export const useAgentExecutionById = (
  executionId: string | null,
  options?: QueryOptions,
) => {
  return useQuery({
    queryKey: ['agent-executions', 'by-id', executionId],
    queryFn: () => fetchAgentExecutionById(executionId!),
    enabled: !!executionId && options?.enabled !== false,
    retry: options?.retry,
  });
};

// Hook for polling execution status
export const useAgentExecutionUpdates = (
  executionId: string | null,
  options?: QueryOptions & { pollingInterval?: number },
) => {
  const { data } = useAgentExecutionById(executionId, options);
  const isRunning = data?.status === 'running' || data?.status === 'pending';

  return useQuery({
    queryKey: ['agent-executions', 'by-id', executionId],
    queryFn: () => fetchAgentExecutionById(executionId!),
    enabled: !!executionId && options?.enabled !== false,
    retry: options?.retry,
    // Poll every 5 seconds if running, otherwise use stale time
    refetchInterval: isRunning ? (options?.pollingInterval ?? 5000) : false,
  });
};

export const useCreateAgentExecution = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createAgentExecution,
    onSuccess: (data) => {
      // Invalidate all agent execution queries to refetch the data
      void queryClient.invalidateQueries({ queryKey: ['agent-executions'] });
      // Set the new execution in cache
      queryClient.setQueryData(
        ['agent-executions', 'by-id', data.agentExecutionId],
        data,
      );
    },
  });
};

export const useUpdateAgentExecution = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      executionId,
      data,
    }: {
      executionId: string;
      data: UpdateAgentExecutionRequest;
    }) => updateAgentExecution(executionId, data),
    onSuccess: (data) => {
      // Update the execution detail cache
      queryClient.setQueryData(
        ['agent-executions', 'by-id', data.agentExecutionId],
        data,
      );
      // Invalidate lists
      void queryClient.invalidateQueries({ queryKey: ['agent-executions'] });
    },
  });
};

// Utility hook for cancelling an execution
export const useCancelAgentExecution = () => {
  const updateMutation = useUpdateAgentExecution();

  return useMutation({
    mutationFn: ({ executionId }: { executionId: string }) =>
      updateMutation.mutateAsync({
        executionId,
        data: { status: 'cancelled' },
      }),
  });
};

// Utility hook for pausing an execution
export const usePauseAgentExecution = () => {
  const updateMutation = useUpdateAgentExecution();

  return useMutation({
    mutationFn: ({ executionId }: { executionId: string }) =>
      updateMutation.mutateAsync({
        executionId,
        data: { status: 'paused' },
      }),
  });
};

// Utility hook for resuming an execution
export const useResumeAgentExecution = () => {
  const updateMutation = useUpdateAgentExecution();

  return useMutation({
    mutationFn: ({ executionId }: { executionId: string }) =>
      updateMutation.mutateAsync({
        executionId,
        data: { status: 'running' },
      }),
  });
};
